<script lang="ts">
  import type { CategoryMeta } from '../categories'
  import { MAP_ICONS, type MapIconKey } from '../mapIcons'
  import Icon from './Icon.svelte'

  // Without `onclick` the marker is a decorative copy, e.g. in a list next to the place's name.
  let {
    icon,
    variant,
    number = null,
    active = false,
    label = null,
    onclick = null,
  }: {
    icon: MapIconKey
    variant: CategoryMeta['variant']
    number?: string | null
    active?: boolean
    label?: string | null
    onclick?: (() => void) | null
  } = $props()
</script>

{#snippet body()}
  <span class="map-marker-circle"><Icon icon={MAP_ICONS[icon]} /></span>
  {#if number}
    <span class="map-marker-number">{number}</span>
  {/if}
{/snippet}

{#if onclick}
  <button
    type="button"
    class={['map-marker', {
      '-active': active,
      '-variant-brand': variant === 'brand',
      '-variant-warning': variant === 'warning',
      '-variant-success': variant === 'success',
    }]}
    aria-label={label}
    aria-pressed={active}
    {onclick}
  >
    {@render body()}
  </button>
{:else}
  <span
    class={['map-marker', {
      '-active': active,
      '-variant-brand': variant === 'brand',
      '-variant-warning': variant === 'warning',
      '-variant-success': variant === 'success',
    }]}
    aria-hidden="true"
  >
    {@render body()}
  </span>
{/if}

<style>
  .map-marker {
    position: relative;
    display: block;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    cursor: pointer;
    transition: transform var(--transition-medium);
  }

  .map-marker:focus-visible {
    outline: none;
  }

  .map-marker.-active {
    transform: scale(1.18);
  }

  .map-marker-circle {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: var(--radius-6);
    border: var(--border-2) solid var(--color-bg);
    box-shadow: 0 2px 6px var(--shadow-1);
    font-size: 15px;
  }

  .map-marker:focus-visible .map-marker-circle {
    box-shadow: 0 0 0 var(--space-2) var(--shadow-1);
  }

  /* Hangs 2px below the circle, so it reads as a caption rather than a notification count. */
  .map-marker-number {
    position: absolute;
    inset-block-start: calc(100% + var(--space-1));
    inset-inline-start: 50%;
    translate: calc(-50% * var(--direction)) 0;
    min-width: 20px;
    padding: 3px 5px;
    border-radius: var(--radius-1);
    border: var(--border-style-2);
    background-color: var(--color-bg);
    box-shadow: 0 1px 3px var(--shadow-1);
    font-size: var(--font-size-3);
    font-weight: var(--font-weight-4);
    line-height: 1;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .map-marker.-variant-brand .map-marker-circle {
    background-color: var(--color-variant-brand);
    color: var(--color-variant-brand-muted);
  }

  .map-marker.-variant-brand.-active .map-marker-circle {
    background-color: var(--color-blue-10);
  }

  .map-marker.-variant-brand .map-marker-number {
    color: var(--color-variant-brand);
  }

  .map-marker.-variant-warning .map-marker-circle {
    background-color: var(--color-variant-warning);
    color: var(--color-variant-warning-muted);
  }

  .map-marker.-variant-warning.-active .map-marker-circle {
    background-color: var(--color-orange-10);
  }

  .map-marker.-variant-warning .map-marker-number {
    color: var(--color-variant-warning);
  }

  .map-marker.-variant-success .map-marker-circle {
    background-color: var(--color-variant-success);
    color: var(--color-variant-success-muted);
  }

  .map-marker.-variant-success.-active .map-marker-circle {
    background-color: var(--color-green-10);
  }

  .map-marker.-variant-success .map-marker-number {
    color: var(--color-variant-success);
  }

  @media (prefers-reduced-motion: reduce) {
    .map-marker {
      transition: none;
    }
  }
</style>
